import axios, { AxiosResponse } from "axios";
import { MerchantResponseDTO } from "./dtos/MerchantResponseDTO";
import { AddressResponseDTO } from "./dtos/AddressResponseDTO";
import { PagedResponseDTO } from "./dtos/PagedResponseDTO";
import MerchantRequestDTO from "./dtos/MerchantRequestDTO";
import MerchantCreateRequestDTO from "./dtos/MerchantCreateRequestDTO";
import MerchantSearchRequestDTO from "./dtos/MerchantSearchRequestDTO";
import MerchantUniqueCheckRequestDTO from "./dtos/MerchantUniqueCheckRequestDTO";
import MerchantUniqueCheckResponseDTO from "./dtos/MerchantUniqueCheckResponseDTO";
import { AddressRequestDTO } from "./dtos/AddressRequestDTO";
import { appConfig } from "../config/keycloak";

/**
 * Servicio para manejar operaciones con Merchants
 */
class MerchantService {
    private readonly api = axios.create({
        baseURL: appConfig.baseUrl
    });

    constructor() {
        // Constructor vacío, la configuración está en la instancia de api
    }

    /**
     * Obtiene la URL base actual desde la configuración en tiempo de ejecución
     * Usado para endpoints que necesitan asegurar la URL correcta del ambiente
     */
    private getBaseURL(): string {
        return appConfig.baseUrl;
    }

    /**
     * Construye la URL completa para un endpoint específico
     */
    private buildURL(endpoint: string): string {
        return `${this.getBaseURL()}${endpoint}`;
    }

    /**
     * Obtener todos los merchants activos con paginación
     */
    public getAllActiveMerchants(
        token: string,
        page: number = 0,
        size: number = 10
    ): Promise<AxiosResponse<PagedResponseDTO<MerchantResponseDTO>>> {
        return this.api.get<PagedResponseDTO<MerchantResponseDTO>>('/merchant-service/api/v2/merchants', {
            headers: { Authorization: `Bearer ${token}` },
            params: {
                page: page,
                size: size
            }
        });
    }

    /**
     * Crear un nuevo merchant (método legacy)
     */
    public async createMerchant(
        token: string,
        payload: MerchantRequestDTO
    ): Promise<AxiosResponse<MerchantResponseDTO>> {
        try {
            const response = await this.api.post<MerchantResponseDTO>(
                '/merchant-service/api/v2/merchants',
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response;
        } catch (error) {
            console.error('❌ Error en createMerchant:', error.response?.status, error.response?.data);
            throw error;
        }
    }

    /**
     * Crear un nuevo merchant con la estructura actualizada (incluye addresses y owners)
     */
    public async createMerchantWithDetails(
        token: string,
        payload: MerchantCreateRequestDTO
    ): Promise<AxiosResponse<MerchantResponseDTO>> {
        try {
            const response = await this.api.post<MerchantResponseDTO>(
                '/merchant-service/api/v2/merchants',
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response;
        } catch (error) {
            console.error('❌ Error en createMerchantWithDetails:', error.response?.status, error.response?.data);
            throw error;
        }
    }

    /**
     * Actualizar un merchant existente (POST /{id})
     */
    public updateMerchant(
        token: string,
        id: number,
        payload: MerchantRequestDTO
    ): Promise<AxiosResponse<MerchantResponseDTO>> {
        return this.api.post<MerchantResponseDTO>(
            `/merchant-service/api/v2/merchants/${id}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Obtener un merchant por ID
     */
    public getMerchantById(
        token: string,
        id: number
    ): Promise<AxiosResponse<MerchantResponseDTO>> {
        return this.api.get<MerchantResponseDTO>(
            `/merchant-service/api/v2/merchants/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Eliminación lógica (soft delete)
     */
    public deleteMerchant(
        token: string,
        id: number
    ): Promise<AxiosResponse<void>> {
        return this.api.delete<void>(
            `/merchant-service/api/v2/merchants/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Búsqueda simple con query string y paginación
     */
    public searchMerchants(
        token: string,
        query: string,
        page: number = 0,
        size: number = 10
    ): Promise<AxiosResponse<PagedResponseDTO<MerchantResponseDTO>>> {
        return this.api.get<PagedResponseDTO<MerchantResponseDTO>>(
            '/merchant-service/api/v2/merchants/search',
            {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    q: query,
                    page: page,
                    size: size
                }
            }
        );
    }

    /**
     * Búsqueda avanzada con filtros usando el endpoint filter
     */
    public filterMerchants(
        token: string,
        filterRequestDto: any,
        page: number = 0,
        size: number = 10
    ): Promise<AxiosResponse<PagedResponseDTO<MerchantResponseDTO>>> {
        return this.api.post<PagedResponseDTO<MerchantResponseDTO>>(
            '/merchant-service/api/v2/merchants/filter',
            filterRequestDto,
            {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    page: page,
                    size: size
                }
            }
        );
    }

    /**
     * Validar unicidad del merchant
     */
    public async validateUnique(
        token: string,
        payload: MerchantUniqueCheckRequestDTO
    ): Promise<AxiosResponse<MerchantUniqueCheckResponseDTO>> {
        const url = this.buildURL('/merchant-service/api/v2/merchants/validate-unique');
        
        console.log('🔗 validateUnique URL:', url);

        try {
            const response = await axios.post<MerchantUniqueCheckResponseDTO>(
                url,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            return response;
        } catch (error) {
            console.error('❌ Error en validateUnique:', error.response?.status, error.response?.data);
            throw error;
        }
    }

    /**
     * Obtener direcciones de un merchant
     */
    public getMerchantAddresses(
        token: string,
        merchantId: number
    ): Promise<AxiosResponse<AddressResponseDTO[]>> {
        return this.api.get<AddressResponseDTO[]>(
            `/merchant-service/api/v2/merchants/${merchantId}/addresses`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Crear una nueva dirección para un merchant
     */
    public createMerchantAddress(
        token: string,
        merchantId: number,
        payload: AddressRequestDTO
    ): Promise<AxiosResponse<AddressResponseDTO>> {
        return this.api.post<AddressResponseDTO>(
            `/merchant-service/api/v2/merchants/${merchantId}/addresses`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Actualizar una dirección de un merchant
     */
    public updateMerchantAddress(
        token: string,
        merchantId: number,
        addressId: number,
        payload: AddressRequestDTO
    ): Promise<AxiosResponse<AddressResponseDTO>> {
        // Backend expects POST to update an address at /merchants/:merchantId/addresses/:addressId
        return this.api.post<AddressResponseDTO>(
            `/merchant-service/api/v2/merchants/${merchantId}/addresses/${addressId}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Eliminar una dirección de un merchant (soft delete)
     */
    public deleteMerchantAddress(
        token: string,
        merchantId: number,
        addressId: number
    ): Promise<AxiosResponse<void>> {
        return this.api.delete<void>(
            `/merchant-service/api/v2/merchants/${merchantId}/addresses/${addressId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Crear un contacto para un owner
     * POST /merchant-service/api/v2/owners/:ownerId/contacts
     */
    public createOwnerContact(
        token: string,
        ownerId: number,
        merchantId: number,
        payload: {
            type: 'PHONE' | 'EMAIL';
            value: string;
            isPrimary: boolean;
        }
    ): Promise<AxiosResponse<any>> {
        return this.api.post<any>(
            `/merchant-service/api/v2/merchants/${merchantId}/owners/${ownerId}/contacts`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Obtener contactos de un owner
     * GET /merchant-service/api/v2/owners/:ownerId/contacts
     */
    public getOwnerContacts(
        token: string,
        ownerId: number
    ): Promise<AxiosResponse<any[]>> {
        return this.api.get<any[]>(
            `/merchant-service/api/v2/owners/${ownerId}/contacts`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Actualizar un contacto de un owner
     * PUT /merchant-service/api/v2/owners/:ownerId/contacts/:contactId
     */
    public updateOwnerContact(
        token: string,
        ownerId: number,
        contactId: number,
        payload: {
            type: 'PHONE' | 'EMAIL';
            value: string;
            isPrimary: boolean;
        }
    ): Promise<AxiosResponse<any>> {
        return this.api.put<any>(
            `/merchant-service/api/v2/owners/${ownerId}/contacts/${contactId}`,
            payload,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }

    /**
     * Eliminar un contacto de un owner
     * DELETE /merchant-service/api/v2/owners/:ownerId/contacts/:contactId
     */
    public deleteOwnerContact(
        token: string,
        ownerId: number,
        contactId: number
    ): Promise<AxiosResponse<void>> {
        return this.api.delete<void>(
            `/merchant-service/api/v2/owners/${ownerId}/contacts/${contactId}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
    }
}

export default new MerchantService();